import { AVUshersTable } from 'src/components/admins-page/av-ushers/subcomponents/AVUshersTable';
import { boardServices } from 'src/services';
import { CartCalendarItem } from '../../admins-page/carts/assignment/calendar/CartCalendarItem';
import { Document, Page } from 'react-pdf';
import { FaArrowLeft, FaArrowRight, FaDownload } from 'react-icons/fa';
import { FailedToGetData, Hr, Loading } from 'src/components/_commons';
import { FC, useEffect, useMemo, useState } from 'react';
import { Groups as PreachingGroups } from 'src/components/admins-page/groups/subcomponents/Groups';
import { MeetingAssignmentsTable } from 'src/components/admins-page/_commons/MeetingAssignmentsTable';
import { millisecondsOneDay, months } from '@monorepo/helpers';
import { PublicMinistryByCalendar } from 'src/components/admins-page/public-ministry/subcomponents/PublicMinistryByCalendar';
import { PublicMinistryByDate } from 'src/components/admins-page/public-ministry/subcomponents/PublicMinistryByDate';
import { PublicMinistryByType } from 'src/components/admins-page/public-ministry/subcomponents/PublicMinistryByType';
import { PublicMinistryRadio, typeOption } from 'src/components/admins-page/public-ministry/subcomponents/PublicMinistryRadio';
import { RoomCleaningTable } from 'src/components/admins-page/room-cleaning/subcomponents/RoomCleaningTable';
import { typeBoardItem, typeBoardSection, typeCartsDoc, typeMeetingsDoc, typePreachingGroup, typePublicMinistryDoc, typePublicMinistryMonthlyService } from '@monorepo/models';
import { useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    cartsDoc: typeCartsDoc | null;
    groups: typePreachingGroup[] | null;
    meetingsDoc: typeMeetingsDoc | null;
    publicMinistryDoc: typePublicMinistryDoc | null;
    section: typeBoardSection;
};

export const BoardSection: FC<propsType> = ({ cartsDoc, groups, meetingsDoc, publicMinistryDoc, section }) => {
    return (
        <div>
            <style>
                {`
                    .react-pdf__Page__canvas {
                        width: 100% !important;
                        height: auto !important;
                    }
                `}
            </style>
            {section.items.sort((i1, i2) => i1.order - i2.order).filter(i => !i.archived).map(item =>
                <div key={item.id} className={'mb-5'}>
                    {item.type === 'avUshers' &&
                        <AVUshers
                            meetingsDoc={meetingsDoc}
                        />
                    }
                    {item.type === 'carts' &&
                        <Carts
                            cartsDoc={cartsDoc}
                        />
                    }
                    {item.type === 'cleaning' &&
                        <Cleaning
                            meetingsDoc={meetingsDoc}
                        />
                    }
                    {item.type === 'file' &&
                        <File
                            item={item}
                            sectionTitle={section.title}
                        />
                    }
                    {item.type === 'groups' &&
                        <Groups
                            groups={groups}
                        />
                    }
                    {item.type === 'midweekMeetings' &&
                        <MidweekMeetings
                            meetingsDoc={meetingsDoc}
                        />
                    }
                    {item.type === 'publicMeetings' &&
                        <PublicMeetings
                            meetingsDoc={meetingsDoc}
                        />
                    }
                    {item.type === 'publicMinistry' &&
                        <PublicMinistry
                            publicMinistryDoc={publicMinistryDoc}
                        />
                    }
                    {item.type === 'text' &&
                        <Text item={item} />
                    }
                </div>
            )}
        </div>
    );
}

const AVUshers = ({ meetingsDoc }: { meetingsDoc: typeMeetingsDoc | null }) => {
    const pm = meetingsDoc?.publics.filter(m => new Date(m.date).getTime() > new Date().getTime() - millisecondsOneDay) ?? [];
    const mm = meetingsDoc?.midweeks.filter(m => new Date(m.date).getTime() > new Date().getTime() - millisecondsOneDay) ?? [];
    const meetings = [...pm, ...mm].sort((m1, m2) => new Date(m1.date).getTime() - new Date(m2.date).getTime());

    if (!meetings) {
        return <FailedToGetData />;
    }

    return (
        <AVUshersTable
            isEdit={false}
            meetings={meetings}
            setMeetingsToSave={() => {}}
            users={[]}
        />
    );
}

const Carts = ({ cartsDoc }: { cartsDoc: typeCartsDoc | null }) => {
    const isDarkMode = useGetIsDarkMode();

    if (!cartsDoc) {
        return <FailedToGetData />;
    }

    return (
        <div className={isDarkMode ? 'bg-dark text-white' : ''}>
            {cartsDoc.points?.filter(p => !p.wasDeleted).map(point =>
                <div key={point.id}>
                    <CartCalendarItem
                        cartsDoc={cartsDoc}
                        month={new Date().getMonth()}
                        point={point}
                        readOnly={true}
                        setRefreshCounter={() => {}}
                        year={new Date().getFullYear()}
                    />
                </div>
            )}
            {new Date().getDate() > 27 &&
                cartsDoc.points?.filter(p => !p.wasDeleted).map(point =>
                    <div key={point.id}>
                        <CartCalendarItem
                            cartsDoc={cartsDoc}
                            month={(new Date().getMonth() + 1) % 12}
                            point={point}
                            readOnly={true}
                            setRefreshCounter={() => {}}
                            year={new Date().getMonth() === 11 ? new Date().getFullYear() + 1 : new Date().getFullYear()}
                        />
                    </div>
                )
            }
        </div>
    );
}

const Cleaning = ({ meetingsDoc }: { meetingsDoc: typeMeetingsDoc | null; }) => {
    const pm = meetingsDoc?.publics.filter(m => new Date(m.date).getTime() > new Date().getTime() - millisecondsOneDay) ?? [];
    const mm = meetingsDoc?.midweeks.filter(m => new Date(m.date).getTime() > new Date().getTime() - millisecondsOneDay) ?? [];
    const meetings = [...pm, ...mm].sort((m1, m2) => new Date(m1.date).getTime() - new Date(m2.date).getTime());

    if (!meetingsDoc) {
        return <FailedToGetData />;
    }

    return (
        <RoomCleaningTable
            isEdit={false}
            meetings={meetings}
            setMeetingsToSave={() => {}}
        />
    );
}

const File = ({ item, sectionTitle }: { item: typeBoardItem; sectionTitle: string; }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    // const dispatch = useDispatch();
    const isMobile = useGetIsMobile();

    useEffect(() => {
        if (!item.bucketName) {
            return;
        }
        setIsLoading(true);
        boardServices.GetPdfFile(sectionTitle, item).then(response => {
            setIsLoading(false);
            if (!response.data?.fileObjectUrl) {
                // dispatch(setAlertResponse({ response }));
                return;
            }
            setObjectUrl(response.data.fileObjectUrl);
        });
    }, []);

    if (isLoading) {
        return <Loading mt={'40px'} />;
    }

    if (!objectUrl) {
        return <FailedToGetData />;
    }

    return (
        <div key={item.id} className={'w-100 h-auto overflow-hidden mt-5'} style={{ backgroundColor: 'lightgrey' }}>
            <Hr />
            {!!numPages &&
                <div className={'d-flex justify-content-between align-items-center my-2 mx-5'}>
                    <button className={`btn btn-general-blue ${isMobile ? 'btn-sm' : ''}`}
                        style={{ width: isMobile ? '60px' : undefined }}
                        onClick={() => setPageNumber(prevPageNumber => Math.max(prevPageNumber - 1, 1))}
                        disabled={pageNumber <= 1}
                    >
                        <FaArrowLeft /> {isMobile ? '' : 'Anterior'}
                    </button>
                    <span className={isMobile ? '' : 'ms-1'}>
                        Página {pageNumber} de {numPages}
                    </span>
                    <button className={`btn btn-general-blue ${isMobile ? 'btn-sm' : ''}`}
                        style={{ width: isMobile ? '60px' : undefined }}
                        onClick={() => setPageNumber(prevPageNumber => Math.min(prevPageNumber + 1, numPages))}
                        disabled={pageNumber >= numPages}
                    >
                        {isMobile ? '' : 'Siguiente'} <FaArrowRight />
                    </button>
                </div>
            }
            <div className={'text-center my-2'}>
                <a href={objectUrl}
                    className={`btn btn-general-red btn-sm ${isMobile ? '' : 'py-2'} px-3`}
                    download={item.originalName}
                >
                    <FaDownload className={'mb-1 me-2'} /> Descargar
                </a>
            </div>
            <div className={'m-3'}>
                <Document
                    file={{ url: objectUrl }}
                    loading={<Loading mt={'90px'} mb={'50px'} />}
                    onLoadSuccess={e => setNumPages(e.numPages)}
                >
                    <Page className={'w-100 h-auto'} pageNumber={pageNumber} scale={1.5} />
                </Document>
            </div>
        </div>
    );
}

const Groups = ({ groups }: { groups: typePreachingGroup[] | null }) => {

    if (!groups) {
        return <FailedToGetData />;
    }

    return (
        <PreachingGroups
            groups={groups}
            numberConfirmed={false}
            readOnly
            setGroups={() => {}}
            users={[]}
        />
    );
}

const MidweekMeetings = ({ meetingsDoc }: { meetingsDoc: typeMeetingsDoc | null; }) => {

    if (!meetingsDoc) {
        return <FailedToGetData />;
    }

    return (
        <MeetingAssignmentsTable
            justFuture
            meetings={meetingsDoc.midweeks}
        />
    );
}

const PublicMeetings = ({ meetingsDoc }: { meetingsDoc: typeMeetingsDoc | null; }) => {

    if (!meetingsDoc) {
        return <FailedToGetData />;
    }

    return (
        <MeetingAssignmentsTable
            justFuture
            meetings={meetingsDoc.publics}
        />
    );
}

const PublicMinistry = ({ publicMinistryDoc }: { publicMinistryDoc: typePublicMinistryDoc | null; }) => {
    const [radioValue, setRadioValue] = useState<typeOption>('byType');
    const isDarkMode = useGetIsDarkMode();

    const monthlyService: typePublicMinistryMonthlyService | null = useMemo(() =>
        publicMinistryDoc?.monthlyServices.find(s =>
            s.year === new Date().getFullYear() && s.month === new Date().getMonth()
        ) ?? null
    , [publicMinistryDoc]);

    if (!publicMinistryDoc || !monthlyService) {
        return <FailedToGetData />;
    }

    return (
        <div className={isDarkMode ? 'text-white' : ''}>
            <PublicMinistryRadio
                radioValue={radioValue}
                setRadioValue={setRadioValue}
            />
            <h2 className={'text-center mb-3'}>
                {months[monthlyService.month].toUpperCase()} {monthlyService.year}
            </h2>
            {radioValue === 'byType' ?
                <PublicMinistryByType
                    monthlyService={monthlyService}
                    publicMinistryDoc={publicMinistryDoc}
                    readOnly
                    setMonthlyService={() => {}}
                    users={[]}
                />
                :
                radioValue === 'byDate'
                ?
                <PublicMinistryByDate
                    monthlyService={monthlyService}
                />
                :
                <PublicMinistryByCalendar
                    monthlyService={monthlyService}
                />
            }
        </div>
    );
}

const Text = ({ item }: { item: typeBoardItem }) => {
    const isDarkMode = useGetIsDarkMode();

    return (
        <div className={`card ${isDarkMode ? 'bg-dark text-white' : ''}`}>
            <h1 className={'p-3'}>
                {item.title}
            </h1>
            <p className={'using-line-breaks ps-3'}>
                {item.content}
            </p>
        </div>
    );
}
